import React, { useState, useEffect } from 'react'
import { Form, Input, Button, App, Select } from 'antd'
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  IdcardOutlined,
  MobileOutlined,
  BookOutlined,
  BankOutlined,
  ArrowLeftOutlined,
  MedicineBoxOutlined,
  TeamOutlined,
  ExperimentOutlined,
  ToolOutlined,
  AuditOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import { useNavigate, Link } from 'react-router-dom'
import apiClient from '../../api/client'
import './Auth.css'

export type RegisterType = 'TEXTBOOK' | 'HOSPITAL'

interface RoleOption {
  id: number
  roleCode: string
  roleName: string
  roleType: string
  description?: string
}

// 医院六者：医、护、药、技、管、法（与后端角色一致）
const HOSPITAL_SIX = [
  { key: 'DOCTOR', name: '医生', icon: <MedicineBoxOutlined />, color: '#1890ff' },
  { key: 'PATIENT', name: '患者', icon: <TeamOutlined />, color: '#52c41a' },
  { key: 'PHARMACIST', name: '药剂师', icon: <ExperimentOutlined />, color: '#722ed1' },
  { key: 'MEDICAL_EQUIPMENT_TECHNICIAN', name: '医疗器械师', icon: <ToolOutlined />, color: '#fa8c16' },
  { key: 'MEDICAL_MANAGER', name: '医疗管理者', icon: <AuditOutlined />, color: '#13c2c2' },
  { key: 'LEGAL_AFFAIRS', name: '法务', icon: <FileTextOutlined />, color: '#eb2f96' }
] as const

type ViewStep = 'module' | 'hospital-roles' | 'form'

const Register: React.FC = () => {
  const { message } = App.useApp()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()

  const [viewStep, setViewStep] = useState<ViewStep>('module')
  const [registerType, setRegisterType] = useState<RegisterType | ''>('')
  const [selectedRoleCode, setSelectedRoleCode] = useState<string | null>(null)
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([])
  const [rolesLoading, setRolesLoading] = useState(false)

  const isHospital = registerType === 'HOSPITAL'
  const isTextbook = registerType === 'TEXTBOOK'
  const isDoctor = selectedRoleCode === 'DOCTOR'
  const fixedRoles = selectedRoleCode ? [selectedRoleCode] : []

  // 教材：进入表单时拉取可选角色；医院：已由六者选定单角色
  useEffect(() => {
    if (viewStep !== 'form' || !isTextbook) return
    let cancelled = false
    setRolesLoading(true)
    apiClient
      .get('/v1/auth/roles/available', { params: { roleType: 'TEXTBOOK' } })
      .then((res: any) => {
        if (cancelled) return
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
        setRoleOptions(list)
      })
      .catch(() => {
        if (!cancelled) message.error('获取角色列表失败')
      })
      .finally(() => {
        if (!cancelled) setRolesLoading(false)
      })
    return () => { cancelled = true }
  }, [viewStep, isTextbook])

  const onFinish = async (values: any) => {
    const roles = isTextbook ? (values.roles || []) : fixedRoles
    if (!roles.length) {
      message.error('请至少选择一个角色')
      return
    }
    setLoading(true)
    try {
      const registerData = {
        username: values.username,
        email: values.email,
        password: values.password,
        registerType,
        roles,
        doctorLicenseNumber: values.doctorLicenseNumber,
        mobile: values.mobile
      }
      const response = await apiClient.post('/v1/auth/register', registerData)
      if (response?.code === 200 || response?.data !== undefined) {
        message.success('注册成功，请登录')
        navigate('/auth/login')
      } else {
        message.error((response as any)?.msg || '注册失败')
      }
    } catch (error: any) {
      const data = error.response?.data
      const errorMsg =
        data?.msg || data?.message || data?.error || data?.detail ||
        (typeof data === 'string' ? data : error.message) || '注册失败'
      message.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const goBack = () => {
    if (viewStep === 'form') {
      setViewStep(isHospital ? 'hospital-roles' : 'module')
      setSelectedRoleCode(null)
      form.resetFields()
    } else if (viewStep === 'hospital-roles') {
      setViewStep('module')
      setRegisterType('')
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--register">
        <div className="auth-header">
          {viewStep !== 'module' && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={goBack}
              className="auth-back-btn"
            >
              返回
            </Button>
          )}
          <h1 className="auth-title">注册</h1>
          <p className="auth-subtitle">
            {viewStep === 'module' && '请选择您的身份类型'}
            {viewStep === 'hospital-roles' && '请选择医院角色'}
            {viewStep === 'form' && (isTextbook ? '教材编写人员注册' : '医院用户注册')}
          </p>
        </div>

        {/* Step 1: 两大独立功能模块 */}
        {viewStep === 'module' && (
          <div className="register-modules">
            <button
              type="button"
              className="register-module register-module--textbook"
              onClick={() => {
                setRegisterType('TEXTBOOK')
                setViewStep('form')
              }}
            >
              <span className="register-module__icon">
                <BookOutlined />
              </span>
              <span className="register-module__title">教材编写人员</span>
              <span className="register-module__desc">参与教材编写、审稿与出版</span>
            </button>
            <button
              type="button"
              className="register-module register-module--hospital"
              onClick={() => {
                setRegisterType('HOSPITAL')
                setViewStep('hospital-roles')
              }}
            >
              <span className="register-module__icon">
                <BankOutlined />
              </span>
              <span className="register-module__title">医院用户</span>
              <span className="register-module__desc">医 / 护 / 药 / 技 / 管 / 法 等</span>
            </button>
          </div>
        )}

        {/* Step 2: 医院六者独立子模块 */}
        {viewStep === 'hospital-roles' && (
          <div className="register-hospital-six">
            <p className="register-hospital-six__hint">请选择您的角色，进入对应注册页面</p>
            <div className="register-hospital-six__grid">
              {HOSPITAL_SIX.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="register-role-card"
                  style={{ ['--role-color' as string]: item.color }}
                  onClick={() => {
                    setSelectedRoleCode(item.key)
                    setViewStep('form')
                    form.resetFields()
                  }}
                >
                  <span className="register-role-card__icon">{item.icon}</span>
                  <span className="register-role-card__name">{item.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: 角色注册表单 */}
        {viewStep === 'form' && (
          <Form
            form={form}
            name="register"
            onFinish={onFinish}
            autoComplete="off"
            size="large"
            className="register-form"
            initialValues={
              selectedRoleCode ? { roles: [selectedRoleCode] } : undefined
            }
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: '请输入用户名' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="用户名" />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '邮箱格式不正确' }
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="邮箱" />
            </Form.Item>

            <Form.Item
              name="mobile"
              label="手机号"
              rules={[
                { pattern: /^(|1[3-9]\d{9})$/, message: '手机号格式不正确' }
              ]}
            >
              <Input prefix={<MobileOutlined />} placeholder="选填：11位手机号" maxLength={11} />
            </Form.Item>

            {isTextbook && (
              <Form.Item name="roles" label="角色" rules={[{ required: true, message: '请选择角色' }]}>
                <Select
                  mode="multiple"
                  placeholder={rolesLoading ? '加载中...' : '请选择角色'}
                  loading={rolesLoading}
                  disabled={rolesLoading}
                  options={roleOptions.map((r) => ({ value: r.roleCode, label: r.roleName }))}
                />
              </Form.Item>
            )}

            {isHospital && (
              <div className="register-form__role-tag">
                当前角色：{HOSPITAL_SIX.find((r) => r.key === selectedRoleCode)?.name ?? selectedRoleCode}
              </div>
            )}

            {isDoctor && (
              <Form.Item
                name="doctorLicenseNumber"
                label="执业医师资格证编号"
                rules={[
                  { required: true, message: '请输入执业医师资格证编号' },
                  { pattern: /^[0-9]{15,20}$/, message: '应为15-20位数字' }
                ]}
              >
                <Input prefix={<IdcardOutlined />} placeholder="15-20位数字" maxLength={20} />
              </Form.Item>
            )}

            <Form.Item
              name="password"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 8, message: '密码至少8位' }
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="密码" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: '请确认密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) return Promise.resolve()
                    return Promise.reject(new Error('两次输入的密码不一致'))
                  }
                })
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="确认密码" />
            </Form.Item>

            <Form.Item className="register-form__submit">
              <Button type="primary" htmlType="submit" block loading={loading} size="large">
                完成注册
              </Button>
            </Form.Item>
          </Form>
        )}

        <div className="auth-footer">
          <span>已有账号？</span>
          <Link to="/auth/login">立即登录</Link>
        </div>
      </div>
    </div>
  )
}

export default Register
